import { SimpleGrid } from "@mantine/core";
// import { map } from "lodash";
import { Link } from "react-router-dom";

import ActionIcon from "../../../../components/ActionIcon/ActionIcon.tsx";
import MultiFieldCard from "../../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgDownload from "../../../../components/Icons/Download";
import { useCurrentUserContext } from "../../../../providers/CurrentUserProvider.tsx";
// import TableFilesDisplay from "../../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import {
  InvoiceDetail,
  toHumanReadablePaymentType,
} from "../../../../types/invoice/invoice.ts";
import { toLocalDate } from "../../../../utils/date.ts";
import { toCurrencyPrice, toLocalNumber } from "../../../../utils/number.ts";
import { toHumanReadable } from "../../../../utils/string.ts";
import { getUserDisplayName, isBababosTenant } from "../../../../utils/user.ts";
import { InvoiceLinesModal } from "../InvoiceLinesModal/InvoiceLinesModal.tsx";
import { useStyles } from "./InvoiceDetailsCards.styles.ts";

interface InvoiceDetailsCardsProps {
  invoice: InvoiceDetail;
}

const InvoiceDetailsCards = ({ invoice }: InvoiceDetailsCardsProps) => {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  // const bastDocuments = invoice?.transactionHistory?.bastDocuments;

  const transactionInformation = [
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "inspectorDocument",
          }}
        >
          BAST and Inspector Document
        </DatabaseMetadataPopover>
      ),
      content: invoice?.inspectorDocument && (
        <Link to={invoice?.inspectorDocument.url} target="_blank">
          <ActionIcon size={16} spaces={false}>
            <SvgDownload />
          </ActionIcon>
        </Link>
      ),
      hidden: !isBababosTenant(user),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceDocument",
          }}
        >
          Inv Document
        </DatabaseMetadataPopover>
      ),
      content: invoice?.invoiceDocument && (
        <Link to={invoice?.invoiceDocument.url} target="_blank">
          <ActionIcon size={16} spaces={false}>
            <SvgDownload />
          </ActionIcon>
        </Link>
      ),
      hidden: !isBababosTenant(user),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "bastDate",
          }}
        >
          BAST Date
        </DatabaseMetadataPopover>
      ),
      content: toLocalDate(invoice?.bastDate),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalInvoice",
          }}
        >
          Total Inv Amount
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.totalInvoice),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceDueDate",
          }}
        >
          Inv Due Date
        </DatabaseMetadataPopover>
      ),
      content: toLocalDate(invoice?.invoiceDueDate),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "paymentType",
          }}
        >
          Payment Type
        </DatabaseMetadataPopover>
      ),
      content: toHumanReadablePaymentType(invoice?.paymentType),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "paymentMethod",
          }}
        >
          Payment Method
        </DatabaseMetadataPopover>
      ),
      content: toHumanReadable(invoice?.paymentMethod),
    },
    // {
    //   label: (
    //     <DatabaseMetadataPopover
    //       databaseMetadata={{
    //         tableName: "TransactionHistory",
    //         fieldName: "bastDocuments",
    //       }}
    //     >
    //       BAST Document
    //     </DatabaseMetadataPopover>
    //   ),
    //   content: bastDocuments?.length ? (
    //     <TableFilesDisplay files={map(bastDocuments, "file")} />
    //   ) : null,
    // },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "poFile",
          }}
        >
          PO File
        </DatabaseMetadataPopover>
      ),
      content: invoice?.transactionHistory?.poFile && (
        <Link to={invoice.transactionHistory.poFile.url} target="_blank">
          <ActionIcon size={16} spaces={false}>
            <SvgDownload />
          </ActionIcon>
        </Link>
      ),
      hidden: !isBababosTenant(user),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "poNumber",
          }}
        >
          PO Number
        </DatabaseMetadataPopover>
      ),
      content: invoice?.transactionHistory?.poNumber,
      hidden: !isBababosTenant(user),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "poDate",
          }}
        >
          PO Date
        </DatabaseMetadataPopover>
      ),
      content: toLocalDate(invoice?.transactionHistory?.poDate),
      hidden: !isBababosTenant(user),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceDetails",
          }}
        >
          Invoice Details
        </DatabaseMetadataPopover>
      ),
      content: invoice?.invoiceDetails?.length ? (
        <InvoiceLinesModal data={invoice.invoiceDetails} />
      ) : null,
      hidden: !isBababosTenant(user),
    },
  ];

  const paymentInformation = [
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalPaid",
          }}
        >
          Total Paid Amount
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.totalPaid),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalAmountDue",
          }}
        >
          OSP Amount
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.totalAmountDue),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoicePayment",
            fieldName: "paidAt",
          }}
        >
          Last Payment Date
        </DatabaseMetadataPopover>
      ),
      content: toLocalDate(invoice?.lastPayment?.paidAt),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "InvoicePayment",
            fieldName: "amount",
          }}
        >
          Last Payment Amount
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.lastPayment?.amount),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "lateCharge",
          }}
        >
          Late Charge
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.lateCharge),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "totalAccountReceivable",
          }}
        >
          Total Account Receivable
        </DatabaseMetadataPopover>
      ),
      content: toCurrencyPrice(invoice?.totalAccountReceivable),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "paymentStatus",
          }}
        >
          Payment Status
        </DatabaseMetadataPopover>
      ),
      content: toHumanReadable(invoice?.paymentStatus),
    },
    {
      label: "Write Off Amount",
      content: toCurrencyPrice(invoice?.writeOffAmount),
    },
  ];

  const collectionInformation = [
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "dpd",
          }}
        >
          DPD
        </DatabaseMetadataPopover>
      ),
      content: toLocalNumber(invoice?.dpd),
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "isRestructured",
          }}
        >
          Is Restructured
        </DatabaseMetadataPopover>
      ),
      content: invoice?.isRestructured ? "YES" : "NO",
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "collectorAssigned",
          }}
        >
          Collector Assigned
        </DatabaseMetadataPopover>
      ),
      content: getUserDisplayName(invoice?.collectorAssigned),
    },

    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
      content: invoice?.salesName,
    },
    {
      label: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "salesTeamName",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
      content: invoice?.salesTeamName,
    },
  ];
  return (
    <SimpleGrid
      cols={3}
      spacing={20}
      verticalSpacing={16}
      className={classes.columnsWrapper}
    >
      <MultiFieldCard
        label="Transaction information"
        variant="blue"
        items={transactionInformation}
        style={{ overflow: "unset!important" }}
      />
      <MultiFieldCard
        label="Payment information"
        variant="blue"
        items={paymentInformation}
      />
      <MultiFieldCard
        label="Collection information"
        variant="blue"
        items={collectionInformation}
      />
    </SimpleGrid>
  );
};

export default InvoiceDetailsCards;
